import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiClusterLinks from "@/components/NaatiClusterLinks";
// r24 Wave 2 — NAATI TH provinces hub index (TH)
import { createFileRoute, Link } from "@tanstack/react-router";
import { NAATI_PROVINCES } from "@/data/naati-th-provinces";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const REGION_ORDER = [
  "กรุงเทพและปริมณฑล", "ภาคเหนือ", "ภาคอีสาน", "ภาคกลาง",
  "ภาคตะวันออก", "ภาคตะวันตก", "ภาคใต้",
] as const;

export const Route = createFileRoute("/translate/naati/city/")({
  head: () => {
    const url = `${SITE}/translate/naati/city`;
    const title = `แปล NAATI 77 จังหวัดทั่วประเทศไทย — ส่ง EMS/DHL ทุกอำเภอ`;
    const desc = `บริการแปล NAATI Certified ครอบคลุมทั้ง ${NAATI_PROVINCES.length} จังหวัด — สำนักงานใหญ่ลาดพร้าว ส่ง EMS ในประเทศ 1-2 วัน ต่างจังหวัดรับ scan ต้นฉบับได้ ไม่ต้องส่งไปรษณีย์เอง — คุยกับผู้เชี่ยวชาญตัวจริงได้เลยวันนี้`;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati/city/index", "th") }, { name: "description", content: ctaDesc(desc, "/translate/naati/city/index", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate/naati/city/index", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/naati/city/index", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: canonicalAndHreflang("/translate/naati/city", "/en/translate/naati/city"),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "ItemList", inLanguage: "th",
          numberOfItems: NAATI_PROVINCES.length,
          itemListElement: NAATI_PROVINCES.map((p, i) => ({
            "@type": "ListItem", position: i + 1, name: `แปล NAATI ${p.th}`,
            url: `${SITE}/translate/naati/city/${p.slug}`,
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "แปล NAATI", item: `${SITE}/translate/naati` },
            { "@type": "ListItem", position: 3, name: "77 จังหวัด", item: url },
          ],
        }) },
      ],
    };
  },
  component: CityHub,
});

function CityHub() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">หน้าแรก</Link> ·{" "}
        <Link to="/translate/naati" className="hover:underline">แปล NAATI</Link> ·{" "}
        <span className="text-slate-700">77 จังหวัด</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold text-navy-deep mb-3">แปล NAATI ทั้ง {NAATI_PROVINCES.length} จังหวัดทั่วประเทศไทย</h1>
      <p data-speakable="true" className="text-lg text-slate-700 mb-8 direct-answer">
        ลูกค้าต่างจังหวัดสั่งแปล NAATI ได้ทุกจังหวัด — สแกนต้นฉบับส่ง LINE ให้เราตรวจก่อน ทีมนักแปล CPN
        ประทับตราที่ลาดพร้าว แล้วส่ง EMS หรือ Kerry ถึงบ้านภายใน 1-2 วันทำการ. เลือกจังหวัดเพื่อดูข้อมูลรับ-ส่งเฉพาะพื้นที่
      </p>
      {REGION_ORDER.map((region) => {
        const provinces = NAATI_PROVINCES.filter((p) => p.region === region);
        if (provinces.length === 0) return null;
        return (
          <section key={region} className="mb-10">
            <h2 className="text-2xl font-semibold text-navy-deep border-b border-gold pb-2 mb-4">
              {region} <span className="text-sm font-normal text-slate-500">({provinces.length} จังหวัด)</span>
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {provinces.map((p) => (
                <li key={p.slug}>
                  <Link
                    to="/translate/naati/city/$slug" params={{ slug: p.slug }}
                    className="block border rounded-md p-2 text-sm hover:border-gold hover:bg-amber-50/50 transition"
                  >
                    <span className="font-medium text-navy-deep">{p.th}</span>
                    <span className="block text-xs text-slate-500">{p.en}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <NaatiClusterLinks />
    <HubLeadCTA locale="th" service="NAATI" />
      </main>
  );
}
